import React, { useState, useEffect } from "react";
import { useAuth } from "../../lib/auth";
import { useContent, persianSlug, uid } from "../../lib/content";
import { formatAfn, getProductPrice, toPersianDigits, parseHashtags, getImageUrl } from "../../lib/api";
import { uploadImage } from "../../lib/storage";
import { Pencil, Trash2, Plus, X, Save, Upload, Image as ImageIcon, Star } from "lucide-react";

const emptyProduct = {
  id: "",
  slug: "",
  name: "",
  category: "honey",
  short_description: "",
  description: "",
  price_afn: "",
  weight: "",
  images: [],
  tags: [],
  in_stock: true,
  featured: false,
};

export default function AdminProducts() {
  const { r2 } = useAuth();
  const { products, categories, saveProducts } = useContent();
  const [editing, setEditing] = useState(null); // null | 'new' | product object
  const [form, setForm] = useState(emptyProduct);
  const [tagsInput, setTagsInput] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && editing) close(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  // Close modal if the item being edited was removed externally
  useEffect(() => {
    if (editing && editing !== "new" && !products.some((p) => p.id === editing.id)) {
      close();
    }
  }, [products]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => {
    setEditing("new");
    setForm({ ...emptyProduct });
    setTagsInput("");
    setError("");
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, price_toman: p.price_toman || "" });
    setTagsInput((p.tags || []).join(", "));
    setError("");
  };
  const close = () => {
    setEditing(null);
    setForm(emptyProduct);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const parsedPrice =
        form.price_afn === "" ? null : Number(form.price_afn);
      if (parsedPrice !== null && (isNaN(parsedPrice) || parsedPrice < 0)) {
        setError("قیمت باید یک عدد مثبت باشد.");
        setSaving(false);
        return;
      }
      const tags = parseHashtags(tagsInput);
      const isNew = editing === "new";
      const id = isNew ? uid() : editing.id;
      const slug = form.slug?.trim() || persianSlug(form.name, id);

      // ensure unique slug
      const slugExists = products.some(
        (p) => p.slug === slug && p.id !== id
      );
      const finalSlug = slugExists ? `${slug}-${id.slice(-4)}` : slug;

      const next = {
        ...emptyProduct,
        ...form,
        id,
        slug: finalSlug,
        price_afn: parsedPrice,
        tags,
        in_stock: !!form.in_stock,
        featured: !!form.featured,
        created_at: isNew ? new Date().toISOString() : editing.created_at,
        updated_at: new Date().toISOString(),
      };
      // Remove legacy field if present
      delete next.price_toman;

      const list = isNew
        ? [next, ...products]
        : products.map((p) => (p.id === id ? next : p));

      await saveProducts(r2, list);
      close();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`آیا از حذف «${p.name}» مطمئن هستید؟`)) return;
    try {
      const list = products.filter((x) => x.id !== p.id);
      await saveProducts(r2, list);
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImg(true);
    setError("");
    const resizeOpts = { maxWidth: 1200, maxHeight: 1200, quality: 0.82 };
    try {
      const res = await uploadImage(r2, file, "images", resizeOpts);
      setForm((f) => ({
        ...f,
        images: [...(f.images || []), { url: res.url, thumb: res.thumbUrl }],
      }));
    } catch (e) {
      setError(`آپلود تصویر ناموفق: ${e.message}`);
    } finally {
      setUploadingImg(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => {
    setForm((f) => ({
      ...f,
      images: f.images.filter((_, i) => i !== idx),
    }));
  };

  return (
    <div data-testid="admin-products-panel">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-xl font-bold text-ink">
          محصولات ({toPersianDigits(products.length)})
        </h2>
        <button
          onClick={openNew}
          className="inline-flex items-center gap-2 bg-honey-500 hover:bg-honey-600 text-white font-display px-5 py-2.5 rounded-full"
          data-testid="admin-product-new-btn"
        >
          <Plus className="w-4 h-4" /> محصول جدید
        </button>
      </div>

      <div className="bg-white border border-honey-200 rounded-2xl overflow-hidden">
        <table className="w-full text-right">
          <thead className="bg-honey-50">
            <tr className="text-sm text-inkMuted font-display">
              <th className="px-5 py-3 font-bold">محصول</th>
              <th className="px-5 py-3 font-bold">دسته</th>
              <th className="px-5 py-3 font-bold">قیمت</th>
              <th className="px-5 py-3 font-bold">وضعیت</th>
              <th className="px-5 py-3 font-bold">عملیات</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-t border-honey-100 text-sm">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-honey-100 shrink-0">
                      {p.images?.[0] && (
                        <img src={getImageUrl(p.images[0], "thumb")} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div>
                      <div className="font-display font-bold text-ink">{p.name}</div>
                      <div className="text-xs text-inkMuted">{p.slug}</div>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3 text-inkMuted">
                  {categories.find((c) => c.key === p.category)?.name_fa || p.category}
                </td>
                <td className="px-5 py-3 persian-numerals">
                  {getProductPrice(p) ? formatAfn(getProductPrice(p)) : "—"}
                </td>
                <td className="px-5 py-3">
                  <div className="flex flex-wrap gap-1">
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        p.in_stock
                          ? "bg-olive-100 text-olive-600"
                          : "bg-red-100 text-red-600"
                      }`}
                    >
                      {p.in_stock ? "موجود" : "ناموجود"}
                    </span>
                    {p.featured && (
                      <span className="text-xs bg-honey-100 text-honey-700 px-2 py-1 rounded-full inline-flex items-center gap-1">
                        <Star className="w-3 h-3" /> ویژه
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-5 py-3">
                  <button
                    onClick={() => openEdit(p)}
                    className="inline-flex items-center gap-1 text-honey-600 hover:text-honey-700 ml-3 font-display"
                    data-testid={`admin-product-edit-${p.slug}`}
                  >
                    <Pencil className="w-4 h-4" /> ویرایش
                  </button>
                  <button
                    onClick={() => remove(p)}
                    className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 font-display"
                    data-testid={`admin-product-delete-${p.slug}`}
                  >
                    <Trash2 className="w-4 h-4" /> حذف
                  </button>
                </td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-10 text-center text-inkMuted">
                  محصولی وجود ندارد.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-50 bg-ink/60 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
          data-testid="admin-product-modal"
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        >
          <form
            onSubmit={submit}
            className="w-full max-w-2xl bg-paper rounded-3xl shadow-xl my-8 overflow-hidden"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-honey-200 bg-white">
              <h3 className="font-display text-lg font-bold text-ink">
                {editing === "new" ? "افزودن محصول" : "ویرایش محصول"}
              </h3>
              <button
                type="button"
                onClick={close}
                className="p-2 rounded-full hover:bg-honey-100"
                aria-label="بستن"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <Field label="نام *" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="admin-product-name" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-display text-inkMuted mb-2">دسته *</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-honey-200 bg-white focus:border-honey-500 focus:outline-none"
                    data-testid="admin-product-category"
                  >
                    {categories.map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.name_fa}
                      </option>
                    ))}
                  </select>
                </div>
                <Field
                  label="قیمت (افغانی)"
                  type="number"
                  value={form.price_afn}
                  onChange={(v) => setForm({ ...form, price_afn: v })}
                  testid="admin-product-price"
                />
              </div>

              <Field label="وزن" value={form.weight} onChange={(v) => setForm({ ...form, weight: v })} placeholder="۹۰۰ گرم" />

              <Field label="Slug (اختیاری)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} placeholder="خودکار از نام ساخته می‌شود" />

              <Field label="توضیح کوتاه" value={form.short_description} onChange={(v) => setForm({ ...form, short_description: v })} />

              <div>
                <label className="block text-sm font-display text-inkMuted mb-2">توضیحات کامل</label>
                <textarea
                  rows={5}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-honey-200 bg-white focus:border-honey-500 focus:outline-none"
                  data-testid="admin-product-description"
                />
              </div>

              <div>
                <label className="block text-sm font-display text-inkMuted mb-2">تصاویر</label>
                <div className="flex flex-wrap gap-3 mb-3">
                  {(form.images || []).map((img, i) => (
                    <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden group">
                      <img src={getImageUrl(img, "thumb")} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        className="absolute top-1 left-1 w-6 h-6 rounded-full bg-ink/70 text-paper grid place-items-center"
                        data-testid={`admin-product-image-remove-${i}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <label className="w-24 h-24 rounded-xl border-2 border-dashed border-honey-300 grid place-items-center text-honey-600 cursor-pointer hover:border-honey-500">
                    {uploadingImg ? (
                      <span className="text-xs">در حال آپلود...</span>
                    ) : (
                      <span className="flex flex-col items-center gap-1 text-xs">
                        <Upload className="w-4 h-4" />
                        آپلود
                      </span>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                      data-testid="admin-product-image-upload"
                    />
                  </label>
                </div>
                <p className="text-xs text-inkMuted flex items-center gap-1">
                  <ImageIcon className="w-3 h-3" />
                  {r2
                    ? "تصاویر در باکت R2 آپلود می‌شوند."
                    : "در حالت محلی، تصاویر به‌صورت data URL ذخیره می‌شوند — برای تست کافی، ولی برای تولید R2 را پیکربندی کنید."}
                </p>
              </div>

              <Field label="برچسب‌ها (با کاما جدا)" value={tagsInput} onChange={(v) => setTagsInput(v)} />

              <div className="flex items-center gap-8 pt-2">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!form.in_stock}
                    onChange={(e) => setForm({ ...form, in_stock: e.target.checked })}
                    data-testid="admin-product-instock"
                  />
                  <span>موجود</span>
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!form.featured}
                    onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                    data-testid="admin-product-featured"
                  />
                  <span>انتخاب سردبیر</span>
                </label>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-white border-t border-honey-200 flex justify-end gap-2">
              <button type="button" onClick={close} className="px-5 py-2.5 rounded-full border border-honey-200 text-ink hover:border-ink">
                انصراف
              </button>
              <button
                type="submit"
                disabled={saving || uploadingImg}
                className="inline-flex items-center gap-2 bg-honey-500 hover:bg-honey-600 text-white font-display font-bold px-5 py-2.5 rounded-full disabled:opacity-60"
                data-testid="admin-product-save"
              >
                <Save className="w-4 h-4" /> {saving ? "در حال ذخیره..." : "ذخیره"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required, testid }) {
  return (
    <div>
      <label className="block text-sm font-display text-inkMuted mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full px-4 py-2.5 rounded-xl border border-honey-200 bg-white focus:border-honey-500 focus:outline-none"
        data-testid={testid}
      />
    </div>
  );
}
